import React, { useCallback, useEffect, useState } from "react";
import { Trash2, Loader2, Check, Camera, X, Lock, Sparkles } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import client, { fileToDataURL } from "@/lib/api";
import { payForBook } from "@/lib/pay";
import UpiPay from "@/components/UpiPay";
import { toast } from "sonner";

// The Year Book jar. One question a week, answered in a sentence; at the end of
// the plan every answer is a page, in order, so the child grows as you turn it.
//
// Questions stack rather than expire — missing a week must cost nothing, or the
// first missed week is the week people quietly stop.
//
// `selectable` turns the same list into the wizard's picker, so the jar and the
// picker can never drift apart.
export default function MemoryJar({ selectable = false, selected = [], onSelect, limit }) {
    const [memories, setMemories] = useState([]);
    const [openQs, setOpenQs] = useState([]);
    const [plan, setPlan] = useState(null);
    const [active, setActive] = useState(null);   // the question being answered
    const [text, setText] = useState("");
    const [toldBy, setToldBy] = useState("");
    const [photo, setPhoto] = useState("");
    const [busy, setBusy] = useState(false);
    const [buying, setBuying] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [method, setMethod] = useState("razorpay");
    const [planKey, setPlanKey] = useState("6m");
    const [upiOrder, setUpiOrder] = useState(null);

    const load = useCallback(() => client.get("/memories").then(({ data }) => {
        setMemories(data.memories || []);
        setOpenQs(data.open_questions || []);
        setPlan(data.plan || null);
    }).catch((e) => {
        // 402 is the paywall, not a failure — the jar is what the plan sells.
        // Ask /subscription anyway: it knows about a UPI payment still being
        // confirmed, and showing the buy button to someone who just paid is how
        // they end up paying twice.
        if (e?.response?.status === 402) {
            return client.get("/subscription")
                .then(({ data }) => setPlan(data))
                .catch(() => setPlan({ active: false }));
        }
        toast.error("Could not open your memory jar.");
    }).finally(() => setLoaded(true)), []);

    useEffect(() => { load(); }, [load]);

    const subscribe = async () => {
        setBuying(true);
        try {
            const order = await payForBook({
                bookId: null, kind: "annual", planKey, method, title: "The Year Book",
            });
            // UPI unlocks nothing here — a human confirms the credit first.
            if (order.upi) { setUpiOrder(order); return; }
            toast.success("Your Year Book has started ✨ First question is in your inbox.");
            await load();
        } catch (e) {
            if (!e?.cancelled) toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
        } finally { setBuying(false); }
    };

    const pickPhoto = async (file) => {
        if (!file) return;
        try { setPhoto(await fileToDataURL(file)); }
        catch (e) { toast.error(e.message); }
    };

    const save = async () => {
        const body = text.trim();
        if (!body) { toast.error("Write the moment first — one sentence is plenty."); return; }
        setBusy(true);
        try {
            const { data } = await client.post("/memories", {
                text: body,
                question_index: active?.index ?? null,
                prompt: active?.question || "",
                told_by: toldBy.trim(),
                photo_b64: photo || null,
            });
            setMemories([data, ...memories]);
            setOpenQs(openQs.filter((q) => q.index !== active?.index));
            setPlan((p) => (p ? { ...p, answered: (p.answered || 0) + 1 } : p));
            setText(""); setToldBy(""); setPhoto(""); setActive(null);
            // Auto-select what you just wrote — you added it to put it in a book.
            if (selectable && onSelect) onSelect([...selected, data.id]);
            toast.success("Kept forever ♥");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save that memory.");
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        setMemories(memories.filter((m) => m.id !== id));
        if (selectable && onSelect) onSelect(selected.filter((s) => s !== id));
        await client.delete(`/memories/${id}`).catch(() => toast.error("Could not delete that"));
        load();   // the question it answered reopens
    };

    const toggle = (id) => {
        if (!onSelect) return;
        if (selected.includes(id)) { onSelect(selected.filter((s) => s !== id)); return; }
        if (limit && selected.length >= limit) {
            toast.error(`Up to ${limit} memories in one book — the rest keep for the next one.`);
            return;
        }
        onSelect([...selected, id]);
    };

    if (!loaded) {
        return <div className="card-cloud p-8 text-center text-plum/40" data-testid="memory-jar-loading">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>;
    }

    // ---- Not subscribed: this is the thing being sold.
    if (plan && !plan.active) {
        const tiers = plan.plans || [];
        const chosen = tiers.find((t) => t.key === planKey) || tiers[0] || {};
        const weeks = chosen.weeks || plan.weeks || 26;
        const rupees = Math.round((chosen.price || plan.price || 159900) / 100).toLocaleString("en-IN");
        return (
            <div className="card-cloud p-6 md:p-8" data-testid="memory-jar-locked">
                <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-plum/50">
                    <Lock className="w-3.5 h-3.5" /> The Year Book
                </div>
                <div className="font-display text-3xl md:text-4xl mt-2 text-plum">
                    One question a week. One book at the end.
                </div>
                <p className="text-plum/60 mt-3 max-w-xl">
                    Every week we email you one question about your child. Answer in a
                    sentence — thirty seconds. At the end, every answer becomes a page in a
                    printed hardcover, in the order it happened, so they grow as you turn it.
                </p>

                {/* How long do you want to keep going? The only real difference is
                    how many weeks — so it is a length choice, not three products. */}
                {tiers.length > 1 && !plan.pending && (
                    <div className="grid sm:grid-cols-3 gap-3 mt-6" data-testid="plan-tiers">
                        {tiers.map((t) => {
                            const on = t.key === planKey;
                            return (
                                <button key={t.key} onClick={() => setPlanKey(t.key)}
                                    data-testid={`plan-tier-${t.key}`}
                                    className={`card-cloud p-4 text-left transition-all hover:scale-[1.02] ${
                                        on ? "ring-4 ring-bubblegum" : ""}`}>
                                    <div className="text-xs uppercase tracking-[0.18em] text-plum/50">
                                        {t.label}
                                    </div>
                                    <div className="font-display text-2xl text-bubblegum mt-1">
                                        ₹{Math.round(t.price / 100).toLocaleString("en-IN")}
                                    </div>
                                    <div className="text-xs text-plum/55 mt-1">
                                        {t.weeks} questions · {t.weeks}-page book
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}

                <ul className="mt-5 grid gap-2 text-sm text-plum/70">
                    <li>· {weeks} questions, emailed weekly — miss one and it just waits</li>
                    <li>· Add that week's photo, so each page looks like they did then</li>
                    <li>· A {weeks}-page hardcover at the end, posted to you</li>
                </ul>
                {/* A payment we're still confirming must never show a buy button —
                    that is how somebody pays for the same year twice. */}
                {plan.pending ? (
                    <div className="mt-7 card-cloud bg-mint/20 p-5" data-testid="plan-pending">
                        <div className="font-display text-xl text-plum">
                            {plan.pending_claimed
                                ? "We're confirming your payment"
                                : "Your UPI payment is waiting"}
                        </div>
                        <p className="text-sm text-plum/60 mt-1">
                            {plan.pending_claimed
                                ? "A human checks the bank and confirms within 24 hours. Your first question arrives the moment it clears — please don't pay again."
                                : "Finish the payment in your UPI app, then tell us with \"I've paid\". Nothing starts until we've confirmed it."}
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-wrap items-center gap-3 mt-7">
                            <div className="inline-flex rounded-full bg-plum/5 p-1" data-testid="plan-method-toggle">
                                {[["razorpay", "Card / Netbanking"], ["upi", "UPI"]].map(([m, label]) => (
                                    <button key={m} onClick={() => setMethod(m)} data-testid={`plan-method-${m}`}
                                        className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                                            method === m ? "bg-white text-plum shadow-sm" : "text-plum/60"}`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <span className="text-xs text-plum/45">
                                {method === "upi"
                                    ? "Pay any UPI app, confirmed by us within 24 hours"
                                    : "Secure checkout via Razorpay"}
                            </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mt-4">
                            <Button onClick={subscribe} disabled={buying} size="lg"
                                data-testid="subscribe-btn"
                                className="rounded-full bg-bubblegum hover:bg-bubblegum/90">
                                {buying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                        : <Sparkles className="w-4 h-4 mr-1" />}
                                Start · {chosen.label || `${weeks} weeks`} · ₹{rupees}
                            </Button>
                            <span className="text-xs text-plum/45">
                                One payment. Covers all {weeks} weeks and the book.
                            </span>
                        </div>
                    </>
                )}
                {plan.book_status === "ready" && (
                    <div className="mt-5 text-sm text-plum/60 border-t border-plum/10 pt-4">
                        Your last plan is finished — we're making that book now.
                    </div>
                )}
                <UpiPay order={upiOrder} open={!!upiOrder}
                    onOpenChange={(o) => { if (!o) { setUpiOrder(null); load(); } }}
                    onClaimed={() => {
                        setUpiOrder(null);
                        toast.success("Thank you — we'll confirm within 24 hours.");
                        load();
                    }} />
            </div>
        );
    }

    const answered = plan?.answered || 0;
    const weeks = plan?.weeks || 26;
    const pct = Math.min(100, Math.round((answered / weeks) * 100));

    return (
        <div data-testid="memory-jar">
            {/* Progress — the whole point is watching this fill up. */}
            <div className="card-cloud p-4 mb-4">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-display text-lg text-plum">
                        {answered} of {weeks} pages kept
                    </div>
                    <div className="text-xs text-plum/50">
                        {openQs.length > 0
                            ? `${openQs.length} question${openQs.length > 1 ? "s" : ""} waiting · nothing expires`
                            : "All caught up — the next one arrives this week."}
                    </div>
                </div>
                <div className="mt-2 h-2 rounded-full bg-plum/10 overflow-hidden">
                    <div className="h-full bg-bubblegum rounded-full transition-all"
                        style={{ width: `${pct}%` }} data-testid="memory-progress" />
                </div>
            </div>

            {/* Open questions, oldest first. They stack; they never expire. */}
            {openQs.length > 0 && !active && (
                <div className="grid gap-2 mb-4" data-testid="open-questions">
                    {openQs.map((q) => (
                        <button key={q.index} onClick={() => setActive(q)}
                            data-testid={`open-question-${q.index}`}
                            className="card-cloud p-4 text-left hover:scale-[1.005] transition-all flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full shrink-0 grid place-items-center bg-lavender/60 text-plum/60 text-xs font-medium">
                                {q.index + 1}
                            </div>
                            <div className="font-display text-lg text-plum flex-1 min-w-0">{q.question}</div>
                        </button>
                    ))}
                </div>
            )}

            {/* Answering one */}
            {active && (
                <div className="card-cloud p-5 md:p-6 mb-4" data-testid="memory-composer">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-xs tracking-[0.2em] uppercase text-plum/50">
                                Question {active.index + 1}
                            </div>
                            <div className="font-display text-xl md:text-2xl mt-1 text-plum">{active.question}</div>
                        </div>
                        <button onClick={() => { setActive(null); setText(""); setPhoto(""); }}
                            aria-label="Close" className="text-plum/30 hover:text-bubblegum shrink-0">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <Textarea value={text} onChange={(e) => setText(e.target.value)}
                        rows={3} maxLength={2000} className="mt-3 bg-white"
                        placeholder="She called the neighbour's cow Biscuit and refused to call it anything else…"
                        data-testid="memory-text" />
                    <div className="flex flex-col sm:flex-row gap-2 mt-3">
                        <Input value={toldBy} onChange={(e) => setToldBy(e.target.value)}
                            maxLength={60} className="bg-white sm:max-w-48"
                            placeholder="Who remembered this?" data-testid="memory-told-by" />
                        <label className="rounded-full px-4 py-2 text-sm border-2 border-plum/15 text-plum/70
                                          hover:bg-white cursor-pointer flex items-center gap-1.5 justify-center">
                            <Camera className="w-4 h-4" />
                            {photo ? "Photo added" : "Add this week's photo"}
                            <input type="file" accept="image/*" className="hidden"
                                data-testid="memory-photo"
                                onChange={(e) => pickPhoto(e.target.files?.[0])} />
                        </label>
                        <Button onClick={save} disabled={busy} data-testid="memory-save"
                            className="rounded-full bg-bubblegum hover:bg-bubblegum/90 sm:ml-auto">
                            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                            Keep this moment
                        </Button>
                    </div>
                    {photo && (
                        <div className="mt-3 flex items-center gap-2">
                            <img src={photo} alt="" className="w-16 h-16 rounded-lg object-cover" />
                            <button onClick={() => setPhoto("")} className="text-xs text-plum/50 hover:text-bubblegum">
                                remove
                            </button>
                            <span className="text-xs text-plum/45">
                                This page will use this photo, so they look their age here.
                            </span>
                        </div>
                    )}
                </div>
            )}

            {loaded && memories.length === 0 && !active && openQs.length === 0 && (
                <div className="text-sm text-plum/50 mt-4 px-1">
                    Your first question is on its way by email.
                </div>
            )}

            {memories.length > 0 && (
                <div className="mt-4 grid gap-2" data-testid="memory-list">
                    {memories.map((m) => {
                        const on = selected.includes(m.id);
                        return (
                            <div key={m.id}
                                onClick={selectable ? () => toggle(m.id) : undefined}
                                data-testid={`memory-${m.id}`}
                                className={`card-cloud p-4 flex items-start gap-3 ${
                                    selectable ? "cursor-pointer transition-all hover:scale-[1.005]" : ""
                                } ${on ? "ring-4 ring-bubblegum" : ""}`}>
                                {selectable && (
                                    <div className={`w-6 h-6 rounded-full shrink-0 grid place-items-center border-2 mt-0.5 ${
                                        on ? "bg-bubblegum border-bubblegum text-white" : "border-plum/20"
                                    }`}>
                                        {on && <Check className="w-3.5 h-3.5" />}
                                    </div>
                                )}
                                {m.photo_b64 && (
                                    <img src={m.photo_b64} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                                )}
                                <div className="min-w-0 flex-1">
                                    {m.prompt && (
                                        <div className="text-[11px] text-plum/40 mb-0.5">{m.prompt}</div>
                                    )}
                                    <div className="text-sm text-plum break-words">{m.text}</div>
                                    <div className="text-[11px] text-plum/45 mt-1">
                                        {[m.question_index != null && `week ${m.question_index + 1}`,
                                          m.told_by && `told by ${m.told_by}`,
                                          m.created_at && new Date(m.created_at).toLocaleDateString()]
                                            .filter(Boolean).join(" · ")}
                                    </div>
                                </div>
                                {!selectable && (
                                    <button onClick={() => remove(m.id)} data-testid={`memory-delete-${m.id}`}
                                        aria-label="Delete this memory"
                                        className="text-plum/30 hover:text-bubblegum shrink-0">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
